import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Meta from "../Meta";
import Loader from "../loader";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { applyLang } from "../../../i18n";
import renderIcon from "../../../components/renderIcon";
import { getBethHabadLabel } from "../utils";

const faqStyles = `
.tr-faq a:focus-visible, .tr-faq button:focus-visible {
  outline: 2px solid var(--primary, #1b579e);
  outline-offset: 4px;
}
.tr-faq__title {
  font-size: clamp(20px, 3.5vw, 32px);
  font-weight: 700;
  color: #1a1a2e;
  text-align: center;
  margin: 64px 0 36px;
}

/* ── Catégories pills ── */
.tr-faq__cats {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
  margin-bottom: 36px;
}
.tr-faq__cat {
  padding: 8px 20px;
  border-radius: 999px;
  border: none;
  background: color-mix(in srgb, var(--primary, #1b579e) 12%, white);
  color: var(--primary, #1b579e);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background .15s, color .15s;
}
.tr-faq__cat:hover {
  background: color-mix(in srgb, var(--primary, #1b579e) 20%, white);
}
.tr-faq__cat--active,
.tr-faq__cat--active:hover {
  background: var(--primary, #1b579e);
  color: white;
}

/* ── Cadre principal ── */
.tr-faq__card {
  max-width: 680px;
  margin: 0 auto;
  background: white;
  border-radius: 20px;
  box-shadow: 0 4px 24px rgba(0,0,0,.08), 0 1px 4px rgba(0,0,0,.04);
  padding: 28px 32px 32px;
}

/* ── Last updated badge ── */
.tr-faq__updated {
  text-align: center;
  margin-bottom: 24px;
}
.tr-faq__updated span {
  display: inline-block;
  background: #fff1f0;
  color: #c0392b;
  font-size: 12px;
  font-weight: 500;
  padding: 4px 12px;
  border-radius: 999px;
}

/* ── Feed bulles ── */
.tr-faq__feed {
  display: flex;
  flex-direction: column;
  gap: 40px;
}
.tr-faq__item {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

/* Question — bulle gris-bleu à gauche */
.tr-faq__q {
  align-self: flex-start;
  background: #eef1f8;
  color: #1a1a2e;
  border-radius: 16px 16px 16px 4px;
  padding: 12px 18px;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;
  max-width: 82%;
}

/* Réponse — bulle blanche bordée + avatar fantôme à droite */
.tr-faq__a-wrap {
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  gap: 10px;
}
.tr-faq__a {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 16px 16px 4px 16px;
  padding: 12px 18px;
  font-size: 14px;
  line-height: 1.6;
  color: #374151;
  max-width: 82%;
  box-shadow: 0 1px 3px rgba(0,0,0,.05);
}
.tr-faq__avatar {
  width: 36px; height: 36px;
  border-radius: 50%;
  background: #e5e7eb;
  flex-shrink: 0;
  object-fit: contain;
  border: 1px solid #e5e7eb;
}

/* Liens dans la réponse */
.tr-faq__links {
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.tr-faq__link {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 13px;
  color: var(--primary, #1b579e);
  text-decoration: none;
  font-weight: 500;
}
.tr-faq__link:hover { text-decoration: underline; }

/* ── Boîte ask ── */
.tr-faq__ask {
  max-width: 680px;
  margin: 20px auto 0;
  border: 1.5px solid #e5e7eb;
  border-radius: 12px;
  padding: 12px 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: white;
  text-decoration: none;
}
.tr-faq__ask-label {
  font-size: 13px;
  color: #9ca3af;
}
.tr-faq__ask-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 7px 16px;
  border-radius: 999px;
  border: none;
  background: var(--primary, #1b579e);
  color: white;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  flex-shrink: 0;
  transition: opacity .15s;
}
.tr-faq__ask-btn:hover { opacity: .85; }

@media (max-width: 640px) {
  .tr-faq__title { width: 90%; margin-left: auto; margin-right: auto; }
  .tr-faq__card { padding: 20px 16px 24px; border-radius: 14px; width: 90%; margin-left: auto; margin-right: auto; }
  .tr-faq__q, .tr-faq__a { max-width: 92%; }
  .tr-faq__ask { width: 90%; margin-left: auto; margin-right: auto; }
  .tr-faq__ask-label { font-size: 12px; }
}
`;

export default function Faq({ context, loading }) {
    const { org, config, travel } = context;

    const faq = travel?.faq || {};
    const allItems = (faq.items || []).map(i => applyLang(i, context.lang));
    const categories = (faq.categories || []).map(c => applyLang(c, context.lang));
    const bethHabadLabel = getBethHabadLabel(travel, context.lang);

    // Garde uniquement les catégories qui ont au moins un item
    const usedCatIds = new Set(allItems.map(i => i.categoryId).filter(Boolean));
    const activeCategories = categories.filter(c => usedCatIds.has(c.id));

    const [activeCat, setActiveCat] = React.useState(null);
    const contactChannels = travel?.bethHabad?.contactChannels || [];
    const whatsapp = contactChannels.find(c => c.type === "whatsapp")?.value;
    const email = contactChannels.find(c => c.type === "email")?.value || org.contact?.email;
    const contactHref = whatsapp
        ? `https://wa.me/${whatsapp.replace(/\D/g, "")}`
        : email ? `mailto:${email}` : org.BASE_WEB_URL + "/travel/tourist-info";

    const visibleItems = activeCat
        ? allItems.filter(i => i.categoryId === activeCat)
        : allItems;

    const lastUpdated = faq.lastUpdatedAt
        ? new Date(faq.lastUpdatedAt).toLocaleDateString(context.langValue || org.language || "fr", { day: "2-digit", month: "2-digit", year: "numeric" })
        : null;

    // Nettoie le texte (strip HTML) pour le JSON-LD FAQPage.
    const stripHtml = (s) => (s || "").replace(/<[^>]*>/g, "").trim();
    const jsonLd = allItems.length > 0 ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: allItems.map(item => ({
            "@type": "Question",
            name: item.question,
            acceptedAnswer: {
                "@type": "Answer",
                text: stripHtml(item.answer),
            },
        })),
    } : null;

    return (
        <div className="travel-page travel-page--faq tr-faq">
            <style dangerouslySetInnerHTML={{ __html: faqStyles }} />
            <Meta
                context={context}
                title={t`FAQ — ${bethHabadLabel} de ${org.ville || org.name}`}
                description={config["meta.description"] || t`Réponses aux questions fréquentes des voyageurs sur le ${bethHabadLabel} de ${org.ville || org.name}`}
                jsonLd={jsonLd}
            />

            {loading && <Loader />}
            <Header context={context} />

            <main className="travel-main">
                <div className="travel-container">

                    <h1 className="tr-faq__title">
                        <Trans>Questions fréquemment posées au sujet de</Trans>{" "}{org.ville || org.name}
                    </h1>

                    {/* Catégories */}
                    {activeCategories.length > 0 && (
                        <div className="tr-faq__cats">
                            {activeCategories.map(cat => (
                                <button
                                    key={cat.id}
                                    type="button"
                                    aria-pressed={activeCat === cat.id}
                                    className={`tr-faq__cat${activeCat === cat.id ? " tr-faq__cat--active" : ""}`}
                                    onClick={() => setActiveCat(activeCat === cat.id ? null : cat.id)}
                                >
                                    {cat.label}
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Cadre principal */}
                    <div className="tr-faq__card">

                        {lastUpdated && (
                            <div className="tr-faq__updated">
                                <span><Trans>Dernière mise à jour le</Trans> {lastUpdated}</span>
                            </div>
                        )}

                        <div className="tr-faq__feed">
                            {visibleItems.map(item => {
                                const links = (item.links || []).map(l => applyLang(l, context.lang)).filter(l => l?.label && l?.url);
                                return (
                                    <div key={item.id} className="tr-faq__item">
                                        <div className="tr-faq__q">{item.question}</div>
                                        <div className="tr-faq__a-wrap">
                                            <div className="tr-faq__a">
                                                {item.answer}
                                                {links.length > 0 && (
                                                    <div className="tr-faq__links">
                                                        {links.map((l, i) => (
                                                            <a key={i} href={l.url} className="tr-faq__link" target="_blank" rel="noopener noreferrer">
                                                                {l.label}
                                                            </a>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                            {org.logo
                                                ? <img src={org.logo} alt={org.name} className="tr-faq__avatar" />
                                                : <div className="tr-faq__avatar" />
                                            }
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                    </div>

                    {/* Boîte ask */}
                    <a className="tr-faq__ask" href={contactHref}>
                        <span className="tr-faq__ask-label">
                            <Trans>Vous avez une question ? Écrivez-nous ici…</Trans>
                        </span>
                        <span className="tr-faq__ask-btn">
                            {renderIcon({ name: "IconSend", source: "tabler" }, 14)}
                        </span>
                    </a>

                </div>
            </main>

            <Footer context={context} />
        </div>
    );
}
